import { AlertTriangle, ArrowRight } from "lucide-react";
import {
  useGetV4ManufacturerByRouteManufacturerIdAccountQuery,
  useGetV4ManufacturerByRouteManufacturerIdDocumentsRequisitionsQuery,
  useGetV4ManufacturerByRouteManufacturerIdInventoryValueQuery,
  useGetV4PartnerByRouteApplicationIdJobsQuery,
  usePutV4ManufacturerByRouteManufacturerIdDraftPurchasesMutation,
  usePutV4PartnerByRouteApplicationIdJobsAssistantTaskMutation,
} from "randmar-api-client";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  DataRefreshAssistantCluster,
  RmBodyZone,
  Skeleton,
  useToast,
} from "randmarcomps";
import * as React from "react";
import { FormattedNumber } from "react-intl";
import { Link } from "react-router-dom";
import JobsGrid from "../components/grids/JobsGrid";
import ManufacturerRequisitionsGrid from "../components/grids/ManufacturerRequisitionsGrid";
import CurrencyUtils from "../components/util/CurrencyUtils";
import FormatDateFromInt from "../components/util/DateTimeUtil";
import InlineSpinner from "../components/util/InlineSpinner";
import RmPageHeader from "../components/layout/RmPageHeader";
import { useManufacturer } from "../contexts/WithManufacturer";
import Path from "../routes/Path";
import withRoot from "../withRoot";

type InsightCard = {
  id: string;
  label: string;
  rows: InsightCardRow[];
};

type InsightCardRow = {
  id: string;
  label: string;
  value?: React.ReactNode;
  helper?: React.ReactNode;
};

function buildDraftPurchasesAssistantPrompt(options: {
  applicationId: string;
  draftsLoaded: number;
  totalDraftValue: number;
  locations: number;
}) {
  return [
    "You are assisting with the Randmar Manufacturer Portal Draft Purchases page.",
    `Manufacturer applicationId: ${options.applicationId}`,
    "",
    `Draft purchases loaded: ${options.draftsLoaded}`,
    `Total draft value: ${options.totalDraftValue}`,
    `Locations represented: ${options.locations}`,
    "",
    "Help me interpret the drafts, find potential issues, and recommend next actions.",
  ].join("\n");
}

const cx = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(" ");

const Requisitions: React.FC = () => {
  const { applicationId } = useManufacturer();
  const { toast } = useToast();

  const [lastRefreshedAt, setLastRefreshedAt] = React.useState<Date | null>(null);

  const manufacturerQuery = useGetV4ManufacturerByRouteManufacturerIdAccountQuery(
    { routeManufacturerId: applicationId },
    { skip: !applicationId }
  );

  const requisitionsQuery = useGetV4ManufacturerByRouteManufacturerIdDocumentsRequisitionsQuery(
    { routeManufacturerId: applicationId },
    { skip: !applicationId }
  );

  const inventoryValueQuery = useGetV4ManufacturerByRouteManufacturerIdInventoryValueQuery(
    { routeManufacturerId: applicationId },
    { skip: !applicationId }
  );

  const jobsQuery = useGetV4PartnerByRouteApplicationIdJobsQuery(
    { routeApplicationId: applicationId },
    { skip: !applicationId }
  );

  const [regenerateDraftPurchases, regenerateDraftPurchasesState] =
    usePutV4ManufacturerByRouteManufacturerIdDraftPurchasesMutation();
  const [queueJobsAssistantTask, queueJobsAssistantTaskState] =
    usePutV4PartnerByRouteApplicationIdJobsAssistantTaskMutation();

  const manufacturer = manufacturerQuery.data;
  const requisitions = requisitionsQuery.data ?? [];
  const inventoryValues = inventoryValueQuery.data;
  const jobs = jobsQuery.data ?? [];

  const isRefreshing =
    manufacturerQuery.isFetching ||
    requisitionsQuery.isFetching ||
    inventoryValueQuery.isFetching ||
    jobsQuery.isFetching ||
    queueJobsAssistantTaskState.isLoading ||
    regenerateDraftPurchasesState.isLoading;

  const hasErrors =
    manufacturerQuery.isError ||
    requisitionsQuery.isError ||
    inventoryValueQuery.isError ||
    jobsQuery.isError;

  const hasLoadedSomeData =
    manufacturerQuery.data !== undefined ||
    requisitionsQuery.data !== undefined ||
    inventoryValueQuery.data !== undefined ||
    jobsQuery.data !== undefined;

  React.useEffect(() => {
    if (!isRefreshing && hasLoadedSomeData) {
      setLastRefreshedAt(new Date());
    }
  }, [hasLoadedSomeData, isRefreshing]);

  React.useEffect(() => {
    const previousTitle = document.title;
    document.title = `Draft Purchases | ${previousTitle}`;
    return () => {
      document.title = previousTitle;
    };
  }, []);

  const handleRefresh = React.useCallback(async () => {
    if (!applicationId) {
      return;
    }
    await Promise.all([
      manufacturerQuery.refetch(),
      requisitionsQuery.refetch(),
      inventoryValueQuery.refetch(),
      jobsQuery.refetch(),
    ]);
    setLastRefreshedAt(new Date());
  }, [applicationId, inventoryValueQuery, jobsQuery, manufacturerQuery, requisitionsQuery]);

  const handleRegenerateDraftPurchases = React.useCallback(async (): Promise<void> => {
    try {
      const success = await regenerateDraftPurchases({ routeManufacturerId: applicationId }).unwrap();

      if (success) {
        toast({
          variant: "success",
          title: "Draft purchases syncing",
          description: "A refresh job has been queued. It will take a moment to populate.",
        });
      } else {
        toast({
          variant: "destructive",
          title: "Could not regenerate drafts",
          description: "The job could not be queued. Try again in a moment.",
        });
      }

      await handleRefresh();
    } catch (error) {
      console.error(error);
      toast({
        variant: "destructive",
        title: "Could not regenerate drafts",
        description: "Something went wrong while queueing the refresh job.",
      });
    }
  }, [applicationId, handleRefresh, regenerateDraftPurchases, toast]);

  const currencyCode = manufacturer?.CurrencyCode?.toUpperCase() || "USD";
  const currencyFormat = React.useMemo(
    () => ({ ...CurrencyUtils.format, currency: currencyCode }),
    [currencyCode]
  );

  const formatDurationString = React.useCallback((value?: string | number | null, unit?: string) => {
    if (value == null) {
      return null;
    }
    const normalized = value.toString().trim();
    if (!normalized) {
      return null;
    }
    if (unit && /^[\d.]+$/.test(normalized)) {
      return `${normalized} ${unit}`;
    }
    return normalized;
  }, []);

  const jobsToUse = React.useMemo(
    () => jobs.filter((job) => job.Header?.Type === "GenerateDraftPurchases"),
    [jobs]
  );
  const hasJobHistory = jobsToUse.length > 0;

  const manufacturerLoading = manufacturer === undefined;
  const manufacturerUnavailable = manufacturer === null;

  const formatCurrencyValue = React.useCallback((value?: number | null) => typeof value === "number"
    ? <FormattedNumber value={value} {...currencyFormat} />
    : null, [currencyFormat]);

  const normalizeTextValue = React.useCallback((value?: string | null) => {
    if (!value || !value.trim()) {
      return null;
    }
    return value.trim();
  }, []);

  const formatEmailValue = React.useCallback((value?: string | null) => {
    const normalized = normalizeTextValue(value);
    if (!normalized) {
      return null;
    }
    return (
      <a
        href={`mailto:${normalized}`}
        className="text-[15px] font-semibold text-slate-900 underline-offset-4 hover:text-[color:var(--ops-accent)] hover:underline focus-visible:rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--color-ring)]"
      >
        {normalized}
      </a>
    );
  }, [normalizeTextValue]);

  const totalInventoryValue = React.useMemo(
    () => {
      if (!inventoryValues) {
        return null;
      }
      return inventoryValues.reduce((sum, value) => sum + (value.InventoryValue || 0), 0);
    },
    [inventoryValues]
  );

  const inventoryLocationCount = React.useMemo(() => {
    const entries = (inventoryValues ?? [])
      .map((entry) => entry.Location)
      .filter((location): location is string => Boolean(location));
    return new Set(entries).size;
  }, [inventoryValues]);

  const snapshotGroups = React.useMemo<InsightCard[]>(() => {
    const creditLimit = typeof manufacturer?.CreditLimit === "number" ? manufacturer.CreditLimit : null;
    const outstanding = typeof manufacturer?.OutstandingBalance === "number" ? manufacturer.OutstandingBalance : null;
    const availableCredit = creditLimit !== null && outstanding !== null ? creditLimit - outstanding : null;
    const utilizationPercent = creditLimit && creditLimit > 0 && outstanding !== null
      ? Math.round((outstanding / creditLimit) * 100)
      : null;

    const automationValue = typeof manufacturer?.AutoUpdate === "boolean"
      ? (manufacturer.AutoUpdate ? "Auto-update on" : "Manual refresh")
      : null;
    const autoReorderDateCopy = manufacturer?.AutoReorderDate
      ? FormatDateFromInt(manufacturer.AutoReorderDate)
      : "Schedule not set";
    const automationHelper = typeof manufacturer?.AutoUpdate === "boolean"
      ? (manufacturer.AutoUpdate
        ? "Drafts refresh automatically."
        : "Use Regenerate to refresh drafts.")
      : "Automation status will appear once account data loads.";

    const creditHeadroomHelper = creditLimit !== null && outstanding !== null
      ? `${CurrencyUtils.formatNumber(outstanding, currencyCode)} in use of ${CurrencyUtils.formatNumber(creditLimit, currencyCode)}`
      : "Set a credit limit to monitor usage.";

    const spendRows: InsightCardRow[] = [
      {
        id: "credit-headroom",
        label: "Credit headroom",
        value: availableCredit !== null ? <FormattedNumber value={availableCredit} {...currencyFormat} /> : null,
        helper: utilizationPercent !== null ? `${Math.max(0, Math.min(utilizationPercent, 999))}% used` : creditHeadroomHelper,
      },
      {
        id: "total-inventory-value",
        label: "Total inventory value",
        value: inventoryValueQuery.isLoading
          ? <Skeleton aria-hidden className="ml-auto h-6 w-24 rounded-lg" />
          : (totalInventoryValue !== null ? <FormattedNumber value={totalInventoryValue} {...currencyFormat} /> : null),
        helper: inventoryValueQuery.isError
          ? "Could not load inventory value."
          : (inventoryLocationCount > 0
            ? `Across ${inventoryLocationCount} location${inventoryLocationCount === 1 ? "" : "s"}.`
            : "Across all locations."),
      },
      {
        id: "outstanding-balance",
        label: "Outstanding balance",
        value: outstanding !== null ? <FormattedNumber value={outstanding} {...currencyFormat} /> : null,
        helper: "Current amount payable.",
      },
      {
        id: "credit-limit",
        label: "Credit limit",
        value: creditLimit !== null ? <FormattedNumber value={creditLimit} {...currencyFormat} /> : null,
        helper: creditLimit !== null ? "Limit applied to draft purchasing." : "Add a credit limit to guide drafts.",
      },
    ];

    const automationRows: InsightCardRow[] = [
      {
        id: "automation-status",
        label: "Status",
        value: automationValue,
        helper: automationHelper,
      },
      {
        id: "next-run",
        label: "Next run",
        value: manufacturer?.AutoUpdate ? autoReorderDateCopy : "On demand",
        helper: manufacturer?.AutoUpdate ? "Follows your automation cadence." : "Drafts refresh when you regenerate.",
      },
      {
        id: "po-email",
        label: "PO email",
        value: formatEmailValue(manufacturer?.POEmail),
        helper: "Notifications about generated POs.",
      },
      {
        id: "currency",
        label: "Currency",
        value: manufacturer?.CurrencyCode?.toUpperCase(),
        helper: "Draft purchases settle in this currency.",
      },
    ];

    const coverageRows: InsightCardRow[] = [
      {
        id: "lead-time",
        label: "Lead time",
        value: formatDurationString(manufacturer?.DefaultLeadTime, "days"),
        helper: "Average days between PO and receipt.",
      },
      {
        id: "stock-window",
        label: "Stock window",
        value: formatDurationString(manufacturer?.DefaultWeeksOfStocking, "weeks"),
        helper: "Target coverage window for drafts.",
      },
      {
        id: "minimum-order",
        label: "Minimum order",
        value: formatCurrencyValue(manufacturer?.DefaultMinimumOrder),
        helper: "Floor spend for generated POs.",
      },
      {
        id: "max-inventory",
        label: "Max inventory",
        value: formatCurrencyValue(manufacturer?.DefaultMaxInventoryValue),
        helper: "Drafts will not exceed this ceiling.",
      },
    ];

    return [
      { id: "automation", label: "Automation", rows: automationRows },
      { id: "coverage-planning", label: "Coverage & guardrails", rows: coverageRows },
      { id: "credit", label: "Credit", rows: spendRows },
    ];
  }, [
    currencyCode,
    currencyFormat,
    formatCurrencyValue,
    formatDurationString,
    formatEmailValue,
    inventoryLocationCount,
    inventoryValueQuery.isError,
    inventoryValueQuery.isLoading,
    manufacturer,
    totalInventoryValue,
  ]);

  const renderMetricValue = React.useCallback((value?: React.ReactNode) => {
    if (manufacturerLoading) {
      return <Skeleton aria-hidden className="h-6 w-24 rounded-lg" />;
    }
    if (
      value === null ||
      value === undefined ||
      (typeof value === "string" && value.trim().length === 0)
    ) {
      return <span className="text-[var(--color-muted-foreground)]">Not set</span>;
    }
    return value;
  }, [manufacturerLoading]);

  const insightsHeadingId = "draft-automation-insights";
  const summaryHeadingId = "draft-location-summary";
  const jobsHeadingId = "draft-jobs-heading";
  const requisitionsHeadingId = "draft-table-heading";

  const draftCount = requisitions.length;
  const { locationSummaries, totalSummary } = React.useMemo(() => {
    const locationMap = new Map<string, { total: number; count: number }>();
    const referenceMap = new Map<string, { total: number; count: number }>();
    let aggregateTotal = 0;
    let aggregateCount = 0;

    requisitions.forEach((req) => {
      const locationKey = req.Location?.trim() || "Unassigned";
      const referenceKey = req.Reference?.trim() || "No reference";
      const subtotal = req.SubTotal || 0;

      aggregateTotal += subtotal;
      aggregateCount += 1;

      const locationStats = locationMap.get(locationKey) || { total: 0, count: 0 };
      locationStats.total += subtotal;
      locationStats.count += 1;
      locationMap.set(locationKey, locationStats);

      const referenceStats = referenceMap.get(referenceKey) || { total: 0, count: 0 };
      referenceStats.total += subtotal;
      referenceStats.count += 1;
      referenceMap.set(referenceKey, referenceStats);
    });

    const mapToArray = (map: Map<string, { total: number; count: number }>) =>
      Array.from(map.entries())
        .map(([label, stats]) => ({
          id: label.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
          label,
          ...stats,
        }))
        .sort((a, b) => b.total - a.total);

    return {
      locationSummaries: mapToArray(locationMap),
      referenceSummaries: mapToArray(referenceMap),
      totalSummary: {
        total: aggregateTotal,
        count: aggregateCount,
      },
    };
  }, [requisitions]);

  const displayedLocationSummaries = locationSummaries;
  const totalLocations = displayedLocationSummaries.length;
  const totalDraftValue = totalSummary.total;

  const assistantPrompt = React.useMemo(
    () =>
      buildDraftPurchasesAssistantPrompt({
        applicationId,
        draftsLoaded: draftCount,
        totalDraftValue,
        locations: totalLocations,
      }),
    [applicationId, draftCount, totalDraftValue, totalLocations]
  );

  const handleQueueJobsAssistantTask = React.useCallback(async (): Promise<void> => {
    if (!applicationId) {
      return;
    }

    const taskPrompt = [
      "Review draft purchase automation health for this manufacturer account.",
      `Application ID: ${applicationId}`,
      `Draft purchases loaded: ${draftCount}`,
      `Total draft value: ${totalDraftValue.toFixed(2)}`,
      `Locations represented: ${totalLocations}`,
      "Focus on stalled jobs, retry candidates, and immediate operator actions.",
    ].join("\n");

    try {
      const queued = await queueJobsAssistantTask({
        routeApplicationId: applicationId,
        createPartnerAssistantJobInput: {
          Prompt: taskPrompt,
        },
      }).unwrap();

      if (queued) {
        toast({
          variant: "success",
          title: "Assistant job queued",
          description: "A jobs assistant task was queued. Refresh to monitor progress.",
        });
      } else {
        toast({
          variant: "destructive",
          title: "Could not queue assistant task",
          description: "The request completed without queueing a task. Try again shortly.",
        });
      }

      await jobsQuery.refetch();
    } catch (error) {
      console.error(error);
      toast({
        variant: "destructive",
        title: "Could not queue assistant task",
        description: "Something went wrong while queueing the task.",
      });
    }
  }, [applicationId, draftCount, jobsQuery, queueJobsAssistantTask, toast, totalDraftValue, totalLocations]);

  const renderSnapshotCard = (insight: InsightCard) => (
    <div key={insight.id} className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
      <h3 className="text-[20px] font-medium text-slate-900">{insight.label}</h3>
      <div className="mt-4 divide-y divide-gray-100">
        {insight.rows.map(row => (
          <div key={row.id} className="flex items-start justify-between gap-3 py-3 first:pt-0 last:pb-0">
            <div className="flex flex-col gap-1">
              <p className="text-[15px] font-semibold text-slate-900">{row.label}</p>
              {row.helper && (
                <p className="text-[13px] text-slate-500">{row.helper}</p>
              )}
            </div>
            <div className="text-right text-[15px] font-semibold leading-tight text-slate-900">
              {renderMetricValue(row.value)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <>
      <RmPageHeader
        title="Draft Purchases"
        subtitle="Review recommended draft purchases, confirm automation inputs, and convert drafts into purchase orders."
        rightTopActions={(
          <DataRefreshAssistantCluster
            className="justify-end"
            lastRefreshAt={lastRefreshedAt}
            isRefreshing={isRefreshing}
            onRefresh={handleRefresh}
            assistantPromptOptions={{ prompt: assistantPrompt }}
          />
        )}
        rightBottomActions={(
          <Link
            to={Path.operationsSubSection(applicationId, "PurchaseOrders")}
            aria-label="View current purchase orders"
            className="w-full md:w-auto"
          >
            <Button variant="default" size="lg" className="group w-full justify-center rounded-xl shadow-sm md:w-auto">
              View Current Purchase Orders
              <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden="true" />
            </Button>
          </Link>
        )}
      />

      <RmBodyZone className="space-y-8" aria-busy={isRefreshing}>
        <main className="mx-auto w-full space-y-8">
          {hasErrors ? (
        <Alert className="rounded-2xl border border-rose-100 bg-rose-50 shadow-sm">
          <AlertTriangle className="h-4 w-4 text-rose-600" aria-hidden="true" />
          <AlertTitle className="text-slate-900">Some data failed to load</AlertTitle>
          <AlertDescription className="text-slate-700">
            Use Refresh to retry. If the problem persists, check Recent sync activity for errors.
          </AlertDescription>
        </Alert>
          ) : null}

          <section aria-labelledby={summaryHeadingId} className="space-y-4">
        {manufacturerUnavailable ? (
          <p className="text-[15px] text-slate-600">
            We couldn&apos;t load automation inputs. Refresh to try again.
          </p>
        ) : (
          <div role="region" aria-labelledby={insightsHeadingId} className="space-y-3">
            <h3 id={insightsHeadingId} className="sr-only">
              Automation, coverage, and guardrails
            </h3>
            <div className="grid gap-4 lg:grid-cols-3" aria-live="polite">
              {snapshotGroups.map((group) => renderSnapshotCard(group))}
            </div>
          </div>
        )}

        {hasJobHistory ? (
          <a
            href={`#${jobsHeadingId}`}
            className="inline-flex items-center text-[15px] font-semibold text-slate-900 underline-offset-4 hover:text-[color:var(--ops-accent)] hover:underline"
          >
            Review sync jobs
            <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
          </a>
        ) : null}
          </section>

          {jobsToUse.length > 0 ? (
            <section className="space-y-3" aria-labelledby={jobsHeadingId}>
          <div className="flex flex-col gap-1">
            <h2 className="text-[24px] font-semibold text-slate-900" id={jobsHeadingId}>
              Recent sync activity
            </h2>
            <p className="text-[15px] text-slate-600">
              Monitor automation jobs to spot blockers before drafting purchase orders.
            </p>
          </div>
          <JobsGrid jobs={jobsToUse} />
            </section>
          ) : null}

          <section className="space-y-3" aria-labelledby={requisitionsHeadingId}>
            <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm" role="region" aria-labelledby={requisitionsHeadingId}>
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div className="flex flex-col gap-1">
              <h2 className="text-[24px] font-semibold text-slate-900" id={requisitionsHeadingId}>
                Draft purchase details
              </h2>
              <p className="text-[15px] text-slate-600">
                Select drafts from the same warehouse to bundle them into a single purchase order.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleQueueJobsAssistantTask}
                disabled={queueJobsAssistantTaskState.isLoading}
                className={cx("rounded-xl", queueJobsAssistantTaskState.isLoading && "pointer-events-none")}
              >
                {queueJobsAssistantTaskState.isLoading ? <InlineSpinner className="mr-2" decorative /> : null}
                Queue Assistant Task
              </Button>
              <Button
                type="button"
                onClick={handleRegenerateDraftPurchases}
                disabled={regenerateDraftPurchasesState.isLoading}
                className={cx("rounded-xl", regenerateDraftPurchasesState.isLoading && "pointer-events-none")}
              >
                {regenerateDraftPurchasesState.isLoading ? <InlineSpinner className="mr-2" decorative /> : null}
                Regenerate Draft Purchases
              </Button>
            </div>
          </div>

          <div className="mt-6">
            <ManufacturerRequisitionsGrid
              requisitions={requisitions}
              currencyFormat={currencyFormat}
              defaultMinimumOrder={manufacturer?.DefaultMinimumOrder ?? null}
              onRequisitionsChanged={handleRefresh}
            />
          </div>
            </div>
          </section>
        </main>
      </RmBodyZone>
    </>
  );
}

export default withRoot(Requisitions);
